import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  Target, 
  TrendingUp, 
  Monitor,
  Tv,
  Radio,
  FileText,
  BarChart3,
  Users
} from 'lucide-react';
import Card from '../../components/UI/Card';
import ContactPreview from '../../components/Home/ContactPreview';

const Advertisements: React.FC = () => {
  const services = [
    {
      icon: Tv,
      title: "Television Commercials",
      description: "Professional TV commercial production for government initiatives like PM Vishwakarma Yojana and Jal Jeevan Mission campaigns, ensuring maximum audience reach and policy awareness."
    },
    {
      icon: Monitor,
      title: "Digital Advertising",
      description: "Comprehensive digital campaigns for tourism promotion, agricultural events like Goa Krishi Mohatsav, and innovation challenges including social media and web-based promotional content."
    },
    {
      icon: Radio,
      title: "Radio Advertisements",
      description: "Engaging radio campaigns for government scheme awareness, tourism promotion, and event publicity including professional voiceovers for multilingual audiences."
    },
    {
      icon: FileText,
      title: "Print & Outdoor Media",
      description: "Creative print campaigns for major events like IFFI, Global Maritime India Summit, trade fairs (IITF 2024), and government policy communications with compelling visual design."
    }
  ];

  const industries = [
    "Government & Public Policy (PM Yojana Campaigns)",
    "Tourism & Cultural Promotion (Goa Tourism)", 
    "Agricultural & Rural Development (Krishi Mohatsav)",
    "International Trade & Commerce (IITF Promotions)",
    "Film & Entertainment Industry (IFFI Campaigns)",
    "Healthcare & Ayurveda (Global Conferences)",
    "Maritime & Shipping (Global Maritime Summit)",
    "Innovation & Technology (Open Innovation Challenge)"
  ];

  return (
    <>
      <Helmet>
        <title>Advertisements - Mirchi Republic Media Production</title>
        <meta name="description" content="Professional advertisement production services including TV commercials, digital campaigns, radio ads, and print media for effective brand communication." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-green-600 to-teal-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <svg viewBox="0 0 800 600" className="w-full h-full" fill="currentColor">
              <defs>
                <pattern id="ad-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                  <circle cx="50" cy="50" r="3" />
                  <circle cx="25" cy="25" r="1.5" />
                  <circle cx="75" cy="75" r="1.5" />
                  <circle cx="25" cy="75" r="1" />
                  <circle cx="75" cy="25" r="1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="800" height="600" fill="url(#ad-pattern)" />
            </svg>
          </div>
          
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1 
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Advertisements
              </motion.h1>
              <motion.p 
                className="text-xl lg:text-2xl opacity-90 max-w-4xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Creating Compelling Brand Stories That Connect, Engage, and Convert
              </motion.p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Creative Advertisement Solutions</h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  We create effective advertising campaigns across TV, digital, radio, and print media. 
                  Our experience includes promotional campaigns for major government initiatives, 
                  cultural festivals like IFFI, and international events like the Global Maritime India Summit.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">Advertisement Services</h2>
                <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
                  Comprehensive advertising solutions across all media channels
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-8 h-full hover:shadow-lg transition-shadow duration-300">
                      <service.icon className="w-16 h-16 text-green-600 mb-6" />
                      <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                      <p className="text-neutral-600 dark:text-neutral-400 leading-relaxed">
                        {service.description}
                      </p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-20 bg-white dark:bg-dark-bg">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-10 lg:col-start-2">
              <motion.div
                className="text-center mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-6">Industries We Serve</h2>
                <p className="text-xl text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
                  Specialized advertising solutions tailored to diverse industry requirements
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {industries.map((industry, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <TrendingUp className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                    <span className="text-sm font-medium">{industry}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12 lg:col-span-8 lg:col-start-3">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl lg:text-5xl font-bold mb-8">Why Choose Our Advertisement Services?</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <Card className="p-6 text-center">
                    <Target className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Strategic Approach</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Data-driven strategies that align with business objectives and target audience insights
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Creative Excellence</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Award-winning creative team delivering innovative and memorable advertising campaigns
                    </p>
                  </Card>
                  <Card className="p-6 text-center">
                    <BarChart3 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-3">Measurable Results</h3>
                    <p className="text-neutral-600 dark:text-neutral-400 text-sm">
                      Performance tracking and analytics to measure campaign effectiveness and ROI
                    </p>
                  </Card>
                </div>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Partner with us to create advertising campaigns that not only capture attention but also 
                  drive meaningful engagement and deliver tangible business results across all media platforms.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <ContactPreview />
      </div>
    </>
  );
};

export default Advertisements;